import { AlertTriangle, CheckCircle, RefreshCw, X } from 'lucide-react'
import { useFlowStore } from '@/stores/flow-store'
import { useFlowValidator } from '@/hooks/use-flow-validator'
import { HeaderWithIcon } from '@/features/flow/_builder/components/ui/header-with-icon'
import SidebarPanelHeading from '../../components/sidebar-panel-heading'
import SidebarPanelWrapper from '../../components/sidebar-panel-wrapper'

export const ValidationErrorsPanel = () => {
  const { validationResult, actions: { validation: actions } } = useFlowStore()
  const [isValidating, validate] = useFlowValidator()

  const handleValidate = () => {
    validate()
  }

  const handleClearErrors = () => {
    actions.clearValidationErrors()
  }

  const totalErrors = validationResult
    ? validationResult.errors.length + validationResult.nodeErrors.length
    : 0

  return (
    <SidebarPanelWrapper>
      <div className='flex flex-col h-full w-80'>
        <SidebarPanelHeading className='shrink-0'>
          <HeaderWithIcon icon='alert-triangle' title='Validation Errors' />
          <div className='flex items-center ml-auto space-x-2'>
            {totalErrors > 0 && (
              <div className='flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-xs font-bold text-white'>
                {totalErrors > 99 ? '99+' : totalErrors}
              </div>
            )}
            <button
              onClick={handleValidate}
              disabled={isValidating}
              className='p-1 transition-colors rounded hover:bg-card-foreground/10 disabled:opacity-50'
              title={isValidating ? 'Validando...' : 'Revalidar fluxo'}
            >
              <RefreshCw
                className={`h-4 w-4 ${isValidating ? 'animate-spin' : ''}`}
              />
            </button>
            {validationResult && (
              <button
                onClick={handleClearErrors}
                className='p-1 transition-colors rounded hover:bg-card-foreground/10'
                title='Limpar resultados'
              >
                <X className='w-4 h-4' />
              </button>
            )}
          </div>
        </SidebarPanelHeading>

        <div className='flex flex-col w-full overflow-y-auto'>
          {!validationResult ? (
            <div className='p-4 text-center text-muted-foreground'>
              <AlertTriangle className='w-8 h-8 mx-auto mb-2 opacity-50' />
              <p className='text-sm'>
                Execute a validação para verificar erros no fluxo
              </p>
              <p className='mt-2 text-xs opacity-70'>
                Use o botão de revalidar no cabeçalho
              </p>
            </div>
          ) : validationResult.isValid ? (
            <div className='p-4 text-center text-green-600'>
              <CheckCircle className='w-8 h-8 mx-auto mb-2' />
              <p className='text-sm font-medium'>Fluxo válido!</p>
              <p className='mt-1 text-xs text-muted-foreground'>
                Nenhum erro encontrado
              </p>
            </div>
          ) : (
            <div className='flex w-full flex-col gap-1 overflow-y-auto p-1.5'>
              {validationResult.errors.map((error, index) => (
                <div
                  key={`general-${index}`}
                  className='flex items-center p-2 transition-colors rounded hover:bg-card-foreground/5'
                >
                  <AlertTriangle className='flex-shrink-0 w-4 h-4 mr-3 text-red-500' />
                  <div className='flex-1 min-w-0'>
                    <p className='text-xs font-medium truncate text-foreground'>
                      Erro Geral
                    </p>
                    <p className='text-xs text-red-600 truncate'>{error}</p>
                  </div>
                </div>
              ))}

              {validationResult.nodeErrors.map((error, index) => (
                <div
                  key={index}
                  className='flex items-center p-2 transition-colors rounded hover:bg-card-foreground/5'
                >
                  <AlertTriangle className='flex-shrink-0 w-4 h-4 mr-3 text-red-500' />
                  <div className='flex-1 min-w-0'>
                    <p className='text-xs font-medium truncate text-foreground'>
                      {error.nodeType} • {error.field}
                    </p>
                    <p className='text-xs text-red-600 truncate'>
                      {error.message}
                    </p>
                    <p className='text-xs text-muted-foreground'>
                      #{error.nodeId.slice(-6)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </SidebarPanelWrapper>
  )
}
